import { useEffect, useRef, useState } from 'react';
import { GripVertical, MessageSquarePlus, Sparkles, Pencil, Replace, Copy, Trash2, Film, Clapperboard } from 'lucide-react';
import { useCanvasStore } from '../store/canvas-store';
import { getRenderHtml, framesById } from '../store/commands';
import { ancestorChain, liveCfElement, type Crumb } from './layerSelect';
import { shortId } from './dropDetect';
import { useTimelineStore } from './timeline/timeline-store';
import { nodeLabel } from './timeline/pickOnCanvas';
import { genCommentId, genMsgId } from '../store/comments';
import { beginLayerTextEdit } from './layerTextEdit';
import { useMediaSwapStore } from './mediaSwap/mediaSwap-store';
import { nodeMediaKind } from '../store/editNode';
import { usePanelStore } from './panel/panel-store';
import { useHoverLayer } from './timeline/useTrackHighlight';
import type { LayerRef } from '../store/types';

interface Box { left: number; top: number; width: number; height: number }

/** Drive a screen-rect Box from a LayerRef via the live cf-element. Shared by the
 *  solid selection outline and the dashed hover outline so geometry isn't dup'd. */
function useLayerBox(ref: LayerRef | null | undefined): Box | null {
  const [box, setBox] = useState<Box | null>(null);
  const raf = useRef(0);
  useEffect(() => {
    if (!ref) {
      setBox(null);
      return;
    }
    const tick = () => {
      const el = liveCfElement(ref.frameId, ref.cfNodeId);
      if (el) {
        const r = el.getBoundingClientRect();
        setBox({ left: r.left, top: r.top, width: r.width, height: r.height });
      } else {
        setBox(null);
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [ref]);
  return box;
}

export function LayerOverlay() {
  const layer = useCanvasStore((s) => s.layerSelection);
  const frames = useCanvasStore((s) => s.doc.frames);
  const setLayer = useCanvasStore((s) => s.setLayerSelection);
  const openSwap = useMediaSwapStore((s) => s.open);
  const hover = useHoverLayer((s) => s.ref);
  const box = useLayerBox(layer);
  // Skip the dashed hover outline when it's the same node as the selection —
  // the solid outline already marks it.
  const hoverDistinct: LayerRef | null =
    hover && !(layer && hover.frameId === layer.frameId && hover.cfNodeId === layer.cfNodeId)
      ? hover
      : null;
  const hoverBox = useLayerBox(hoverDistinct);

  if ((!layer || !box) && !hoverBox) return null;

  const selection = layer && box ? renderSelection(layer, box) : null;

  return (
    <div className="pointer-events-none fixed inset-0 z-30">
      {hoverBox && (
        <div
          className="absolute border-2 border-dashed border-[var(--color-accent)] rounded-[2px] opacity-60"
          style={{ left: hoverBox.left, top: hoverBox.top, width: hoverBox.width, height: hoverBox.height }}
        />
      )}
      {selection}
    </div>
  );

  function renderSelection(layer: LayerRef, box: Box) {
    const frame = frames.find((f) => f.id === layer.frameId);
    const crumbs: Crumb[] = frame
      ? ancestorChain(getRenderHtml(frame, framesById(frames)), layer.cfNodeId)
      : [];
    const liveEl = liveCfElement(layer.frameId, layer.cfNodeId);
    const mediaKind = liveEl ? nodeMediaKind(liveEl) : 'none';
    const isLottie = !!liveEl?.hasAttribute('data-lottie');

    return (
      <>
      <div
        className="absolute border-2 border-[var(--color-accent)] rounded-[2px]"
        style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
      />
      <div
        className="absolute pointer-events-auto flex items-center gap-2 -translate-y-full pb-1"
        style={{ left: box.left, top: box.top }}
      >
        <div className="flex items-center gap-1 rounded-[4px] bg-[var(--color-surface)] shadow-[var(--shadow-2)] px-2 py-1 font-mono text-[11px] text-[var(--color-text-muted)]">
          {crumbs.map((c, i) => (
            <button
              key={c.cfNodeId}
              className="hover:text-[var(--color-text)]"
              onClick={() => setLayer({ frameId: layer.frameId, cfNodeId: c.cfNodeId })}
            >
              {c.tag}
              {i < crumbs.length - 1 ? ' ›' : ''}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 rounded-[4px] bg-[var(--color-surface)] shadow-[var(--shadow-2)] px-2 py-1 font-sans text-[11px]">
          <button
            title="Drag to reorder"
            aria-label="Drag to reorder layer"
            className="cursor-grab text-[var(--color-text-muted)] hover:text-[var(--color-text)] active:cursor-grabbing"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
              useCanvasStore.getState().setLayerDrag({ frameId: layer.frameId, cfNodeId: layer.cfNodeId });
            }}
          ><GripVertical size={14} /></button>
          <button
            title="Comment"
            aria-label="Add comment"
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            onClick={() => {
              const id = genCommentId();
              useCanvasStore.getState().dispatch({
                t: 'comment.create', id,
                target: { kind: 'element', frameId: layer.frameId, nodeId: layer.cfNodeId },
                message: { id: genMsgId(), author: 'user', text: '', ts: Date.now() },
              });
              useCanvasStore.getState().setOpenCommentThread(id);
            }}
          ><MessageSquarePlus size={14} /></button>
          <button
            title="Ask Viv"
            aria-label="Ask Viv about this layer"
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            onClick={() => {
              // keep layerSelection (scope line + subtree flow to the agent),
              // open the frame-scoped panel chat — visible + streaming + user-typed.
              usePanelStore.getState().setChatGlobalOverride(false);
              usePanelStore.getState().setOpen(true);
            }}
          ><Sparkles size={14} /></button>
          <button
            title="Animate"
            aria-label="Animate this layer"
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            onClick={() => {
              const st = useCanvasStore.getState();
              const f = st.doc.frames.find((fr) => fr.id === layer.frameId);
              const tl = f && 'motion' in f ? f.motion : undefined;
              st.beginTransaction('animate layer');
              try {
                if (!tl) st.dispatch({ t: 'motion.init', frameId: layer.frameId });
                const has = tl?.tracks.some((tr) => tr.target === layer.cfNodeId);
                if (!has) {
                  const el = liveCfElement(layer.frameId, layer.cfNodeId);
                  st.dispatch({
                    t: 'motion.addTrack',
                    frameId: layer.frameId,
                    trackId: shortId(),
                    layerId: shortId(),
                    target: layer.cfNodeId,
                    label: el ? nodeLabel(el) : layer.cfNodeId,
                  });
                }
              } finally {
                st.endTransaction();
              }
              useTimelineStore.getState().setOpen(true);
            }}
          ><Film size={14} /></button>
          {isLottie && (
            <button
              title="Animation"
              aria-label="Edit Lottie animation"
              className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              onClick={() => {
                // Keep layerSelection (the panel's Animation section reads it) and
                // open the Editor Panel where LottieControls is hosted.
                usePanelStore.getState().setChatGlobalOverride(false);
                usePanelStore.getState().setOpen(true);
              }}
            ><Clapperboard size={14} /></button>
          )}
          <button
            title="Edit text"
            aria-label="Edit layer text"
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            onClick={() => { if (frame) beginLayerTextEdit(frame, layer.cfNodeId); }}
          ><Pencil size={14} /></button>
          {mediaKind !== 'none' && (
            <button
              title="Swap media"
              aria-label="Swap media"
              className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              onClick={() => openSwap(layer.frameId, layer.cfNodeId)}
            ><Replace size={14} /></button>
          )}
          {(frame?.kind === 'free' || frame?.kind === 'main') && (
            <>
              <button
                title="Duplicate"
                aria-label="Duplicate layer"
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                onClick={() => useCanvasStore.getState().dispatch({ t: 'frame.duplicateNode', frameId: layer.frameId, cfNodeId: layer.cfNodeId })}
              ><Copy size={14} /></button>
              <button
                title="Delete"
                aria-label="Delete layer"
                className="text-[var(--color-danger)] hover:opacity-80"
                onClick={() => {
                  useCanvasStore.getState().dispatch({ t: 'frame.deleteNode', frameId: layer.frameId, cfNodeId: layer.cfNodeId });
                  useCanvasStore.getState().setLayerSelection(null);
                }}
              ><Trash2 size={14} /></button>
            </>
          )}
        </div>
      </div>
      </>
    );
  }
}
